import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type RowData,
  type RowSelectionState,
  type Updater,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

// Extend TanStack Table meta
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    className?: string;
  }
}

import { ExternalLink, Loader } from 'lucide-react';

import { type Device, loraApi, type PaginatedResponse } from '@/api/connectivity/lora';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/context/auth-context';
import { toast } from '@/hooks/use-toast';
import { getApiErrorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';
import { getBatchResultToast } from '@/utils/lora-batch-result';

// Device model labels
const MODEL_LABELS: Record<string, string> = {
  DM_BWS_LORA: 'DM BWS LoRa',
  DM_BWS_NB2_LORA: 'DM BWS NB2 LoRa',
  DM_BWS_LL_NB: 'DM BWS LL NB',
  DM_BWS_LL_NB_LORA: 'DM BWS LL NB+LoRa',
  DM_BWS_BAIT_NB: 'DM BWS BAIT NB',
  DM_BWS_BAIT_NB_LORA: 'DM BWS BAIT NB+LoRa',
  UNRECOGNIZED: 'Não reconhecido',
};

interface DeviceActivationTableProps {
  data: PaginatedResponse<Device>;
  activate: boolean;
  currentPage?: number;
  pageParamName?: 'activatedPage' | 'deactivatedPage';
  onPageChange: (page: number) => void;
}

export function DeviceActivationTable({
  data,
  activate,
  currentPage = 1,
  onPageChange,
}: DeviceActivationTableProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { docs, pages = 1, total = 0, limit = 20 } = data;

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  function onRowSelectionChange(updater: Updater<RowSelectionState>) {
    setRowSelection(updater);
  }

  const selectedRows = useMemo(() => {
    return docs.filter((row) => rowSelection[row.id]);
  }, [docs, rowSelection]);

  const hasMultipleSelection = selectedRows.length >= 2;

  // Single device activation mutation
  const activateMutation = useMutation({
    mutationKey: ['lora-toggle-device-activation'],
    mutationFn: async (device: Device) => {
      if (!user) throw new Error('Usuário não autenticado');

      await loraApi.activate({
        type: activate ? 'deactivate' : 'activate',
        device: {
          bcube_device_id: device.id,
          kora_device_id: device.equipment.lora_keys?.kora_id,
          serial: device.equipment.serial,
        },
        author: {
          user_uuid: user.id,
          name: user.name,
        },
      });
    },
    onSuccess: () => {
      toast({
        title: 'Sucesso!',
        description: 'Sua requisição foi enviada com sucesso!',
      });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: (error: Error) => {
      toast({
        title: `Não foi possível ${activate ? 'desativar' : 'ativar'} o dispositivo`,
        description: getApiErrorMessage(error, error.message),
        variant: 'destructive',
      });
    },
  });

  // Batch activation mutation
  const batchActivateMutation = useMutation({
    mutationKey: ['lora-batch-toggle-device-activation'],
    mutationFn: async (devices: Device[]) => {
      if (!user) throw new Error('Usuário não autenticado');

      const serials = devices
        .filter((d) => d.equipment.lora_keys?.kora_id)
        .map((d) => d.equipment.serial);

      return loraApi.batchActivate({
        type: activate ? 'deactivate' : 'activate',
        batch_metadata: [
          {
            type: 'checkbox',
            serials,
          },
        ],
        author: {
          user_uuid: user.id,
          name: user.name,
        },
      });
    },
    onSuccess: (data) => {
      toast(getBatchResultToast(data.results, activate ? 'deactivate' : 'activate'));
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setRowSelection({});
    },
    onError: (error: Error) => {
      toast({
        title: `Não foi possível ${activate ? 'desativar' : 'ativar'} os dispositivos`,
        description: getApiErrorMessage(error, error.message),
        variant: 'destructive',
      });
    },
  });

  // Table columns
  const columns: ColumnDef<Device>[] = useMemo(
    () => [
      {
        id: 'select-column',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            onCheckedChange={(checked) => table.toggleAllRowsSelected(!!checked)}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onCheckedChange={(checked) => row.toggleSelected(!!checked)}
          />
        ),
      },
      {
        header: 'Serial',
        accessorKey: 'serial',
        cell: ({ row }) => row.original.equipment.serial,
      },
      {
        header: 'Modelo',
        accessorKey: 'product',
        cell: ({ row }) => MODEL_LABELS[row.original.model] ?? row.original.model,
      },
      {
        header: 'Data de criação',
        accessorKey: 'created_at',
        cell: ({ row }) => {
          try {
            return new Date(row.original.created_at).toLocaleString('pt-BR');
          } catch {
            return '--';
          }
        },
        meta: {
          className: 'w-40',
        },
      },
      {
        id: 'action',
        header: '',
        cell: ({ row }) => {
          const device = row.original;
          const isKoraIdEmpty = !device.equipment.lora_keys?.kora_id;
          const hasPendingJob = device.equipment.lora_keys?.kora_job_scheduled_id;

          // Show pending job link
          if (hasPendingJob) {
            return (
              <Link to={`/connectivity/lora-activation-logs?serial=${device.equipment.serial}`}>
                <Button className="flex" variant="link">
                  Processamento pendente
                  <ExternalLink size={12} />
                </Button>
              </Link>
            );
          }

          // When multiple rows are selected, only show batch button on first selected row
          if (hasMultipleSelection) {
            const isFirstSelected = selectedRows[0]?.id === device.id;
            if (isFirstSelected) {
              const hasInvalidDevices = selectedRows.some((d) => !d.equipment.lora_keys?.kora_id);
              const hasPendingDevices = selectedRows.some(
                (d) => d.equipment.lora_keys?.kora_job_scheduled_id,
              );
              const isBlocked = hasInvalidDevices || hasPendingDevices;

              return (
                <TooltipProvider>
                  <Tooltip>
                    {/* Disabled buttons don't emit pointer events, so the span is the trigger */}
                    <TooltipTrigger asChild>
                      <span tabIndex={isBlocked ? 0 : -1} className="inline-flex">
                        <Button
                          size="sm"
                          disabled={isBlocked || batchActivateMutation.isPending}
                          onClick={() => batchActivateMutation.mutate(selectedRows)}
                          variant="ghost"
                        >
                          {batchActivateMutation.isPending && (
                            <Loader className="animate-spin" size={12} />
                          )}
                          {batchActivateMutation.isPending
                            ? activate
                              ? 'Desativando...'
                              : 'Ativando...'
                            : `${activate ? 'Desativar' : 'Ativar'} (${selectedRows.length})`}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {isBlocked && (
                      <TooltipContent>
                        {hasInvalidDevices ? (
                          <>
                            Alguns dispositivos selecionados não possuem ID da KORA.
                            <br />
                            Verifique se eles foram criados corretamente na KORA.
                          </>
                        ) : (
                          <>
                            Alguns dispositivos selecionados possuem um processamento pendente.
                            <br />
                            Remova-os da seleção ou aguarde a finalização.
                          </>
                        )}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              );
            }
            return null;
          }

          // Single selection - show individual button
          // The mutation is shared by every row, so only the submitted row shows the spinner
          const isThisRowPending =
            activateMutation.isPending && activateMutation.variables?.id === device.id;

          return (
            <TooltipProvider>
              <Tooltip>
                {/* Disabled buttons don't emit pointer events, so the span is the trigger */}
                <TooltipTrigger asChild>
                  <span tabIndex={isKoraIdEmpty ? 0 : -1} className="inline-flex">
                    <Button
                      size="sm"
                      disabled={isKoraIdEmpty || activateMutation.isPending}
                      onClick={() => activateMutation.mutate(device)}
                      variant="ghost"
                    >
                      {isThisRowPending && <Loader className="animate-spin" size={12} />}
                      {isThisRowPending
                        ? activate
                          ? 'Desativando...'
                          : 'Ativando...'
                        : activate
                          ? 'Desativar'
                          : 'Ativar'}
                    </Button>
                  </span>
                </TooltipTrigger>
                {isKoraIdEmpty && (
                  <TooltipContent>
                    Não é possível {activate ? 'desativar' : 'ativar'} esse dispositivo: ID da KORA
                    não identificado.
                    <br />
                    Verifique se ele foi criado corretamente na KORA.
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          );
        },
        meta: {
          className: 'w-40',
        },
      },
    ],
    [activate, selectedRows, hasMultipleSelection, activateMutation, batchActivateMutation],
  );

  const table = useReactTable({
    data: docs,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange,
    getRowId: (row) => row.id,
    state: {
      rowSelection,
    },
  });

  return (
    <div className="space-y-4 grid grid-rows-[1fr_min-content]">
      <div className="border-t w-full overflow-y-auto !h-full">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(header.column.columnDef.meta?.className)}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow className="hover:bg-background" key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Nenhum dado encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {pages > 1 && (
        <div className="overflow-hidden rounded-md">
          <Pagination
            currentPage={currentPage}
            totalPages={pages}
            totalItems={total}
            limit={limit}
            onPageChange={onPageChange}
            className="border-0 p-1"
          />
        </div>
      )}
    </div>
  );
}
